/**
 * The guest-tag vocabulary, kept out of the components so it can be tested
 * without a render harness.
 *
 * A tag is a set membership: "college friends", "vegetarian", "wheelchair". It
 * is host-authored free text, but it is reified on a slug rather than stored as
 * raw text, because free text produces "College Friends", "college friends" and
 * "College" on one roster, after which no filter works and no count is right.
 * The slug is the canonical name; the label is what the host typed.
 *
 * Two rules here are load-bearing and easy to lose:
 *
 *  1. The slug fold has to match the server's. The API canonicalizes with
 *     `GroupsController.NormalizeSegment`, so {@link tagSlug} is that function
 *     and nothing else — a browser that folds differently mints a second tag
 *     for a name the host believes already exists.
 *  2. The apostrophe fold runs FIRST. NormalizeSegment leaves apostrophes
 *     alone, so "The O’Briens" (curly, the Apple default) and "The O'Briens"
 *     (straight) are two different slugs with two different member sets. That is
 *     the same defect that ate the RSVP import, and `normalizeCsvText` already
 *     exists to fix it.
 *
 * Everything a host can act on — counts, colours, what a bulk apply will do —
 * is computed here so the sentence on screen and the request on the wire are
 * derived from one function rather than written twice.
 */
import { normalizeCsvText } from './partifulCsv';
import type { GuestTag, TrackerGuest } from '../services/tracker-api-client';
// The mode is the wire contract, so it is defined once beside the endpoint that
// reads it and re-exported here for the components that plan against it.
import type { TagAssignMode } from '../services/tags-api-client';

export type { TagAssignMode };

/**
 * The kinds the database CHECK-constrains. Order matters: it is the order the
 * palette groups them in and the order chips render on a row, and dietary and
 * access come first because a nut allergy has to be the first thing seen.
 */
export const TAG_KINDS = ['dietary', 'access', 'household', 'group', 'logistics'] as const;
export type TagKind = (typeof TAG_KINDS)[number];

export const TAG_KIND_LABELS: Readonly<Record<TagKind, string>> = {
  dietary: 'Food & drink',
  access: 'Access & mobility',
  household: 'Households',
  group: 'Groups',
  logistics: 'Logistics',
};

/**
 * The two kinds a host must never be able to text as an audience, and the two
 * that carry health information. Kept as one list because it is one fact: these
 * are about a person's body, not their social circle.
 */
export const HEALTH_TAG_KINDS: readonly TagKind[] = ['dietary', 'access'];

export function isHealthKind(kind: string): boolean {
  return (HEALTH_TAG_KINDS as readonly string[]).includes(kind);
}

/** The kind an unrecognised value falls back to, matching the column default. */
export const DEFAULT_TAG_KIND: TagKind = 'group';

export function asTagKind(value: string | null | undefined): TagKind {
  const kind = String(value ?? '').trim().toLowerCase();
  return (TAG_KINDS as readonly string[]).includes(kind) ? (kind as TagKind) : DEFAULT_TAG_KIND;
}

/**
 * The canonical tag name, folded exactly as the server folds it.
 *
 * `NormalizeSegment` is: trim, collapse whitespace and hyphen runs to '_',
 * lowercase. The `normalizeCsvText` pass in front of it is the fix for the
 * curly-apostrophe defect described at the top of this file — it also collapses
 * non-breaking spaces, which a roster pasted out of Partiful is full of.
 *
 * Returns '' for anything blank. The caller decides what a blank means; here it
 * is simply not a tag.
 */
export function tagSlug(label: string | null | undefined): string {
  const folded = normalizeCsvText(String(label ?? ''));
  if (!folded) return '';
  return folded.replace(/[\s-]+/g, '_').toLowerCase();
}

/** The column is VARCHAR(50), same as `segment`. A longer slug is a 400. */
export const TAG_SLUG_MAX = 50;
export const TAG_LABEL_MAX = 80;
/** `POST /tags/assign` caps the batch server-side; the UI must not exceed it. */
export const TAG_ASSIGN_MAX_GUESTS = 500;
export const TAG_ASSIGN_MAX_TAGS = 10;

/**
 * Why a label cannot become a tag, in words a host can act on, or '' when it
 * can. Checked before the request so a refusal does not cost a round trip and
 * does not read as a server error.
 */
export function tagLabelProblem(
  label: string,
  existing: ReadonlyArray<GuestTag> = [],
): string {
  const trimmed = label.trim();
  if (!trimmed) return 'Give the tag a name.';
  if (trimmed.length > TAG_LABEL_MAX) return `Keep the name under ${TAG_LABEL_MAX} characters.`;
  const slug = tagSlug(trimmed);
  if (!slug) return 'That name has no letters or numbers in it.';
  if (slug.length > TAG_SLUG_MAX) return `Keep the name under ${TAG_SLUG_MAX} characters.`;
  const clash = existing.find((tag) => tag.slug === slug);
  // Not an error the host caused — two spellings of one name are the whole
  // reason slugs exist — so it names the tag they already have.
  if (clash) return `“${clash.label}” is already that tag.`;
  return '';
}

/**
 * The kind a create sends, or '' for "let the server decide from the name".
 *
 * '' is not a kind — it is the absence of one, and it exists because the server
 * knows something the browser does not. `GroupGuestTagVocabulary` maps
 * "Wheelchair" onto an ACCESS tag that is critical by default, and that
 * fallback only runs when `kind` is absent from the body. A browser that always
 * sends a kind overrules it silently: "Wheelchair" arrives as a `group` tag
 * still carrying the seed's critical flag, and the server refuses the pair with
 * a 400 naming a flag the host never touched.
 */
export type CreateTagKind = TagKind | '';

/** The Auto option: no kind on the wire, so the seeded vocabulary decides. */
export const AUTO_TAG_KIND: CreateTagKind = '';

export interface NewTagRequest {
  label: string;
  kind?: TagKind;
}

/**
 * The body a create sends.
 *
 * `kind` is OMITTED rather than defaulted when the host left the dropdown on
 * Auto: absent and 'group' are two different requests, and only the absent one
 * lets the server's seeded definition apply. This is the one place that
 * decision is made, so the palette cannot quietly acquire a default of its own.
 */
export function newTagRequest(label: string, kind: CreateTagKind): NewTagRequest {
  const trimmed = label.trim();
  return kind ? { label: trimmed, kind } : { label: trimmed };
}

/** The fields of a tag this UI patches, exactly as they go on the wire. */
export interface TagPatchFields {
  kind?: TagKind;
  isCritical?: boolean;
}

/** A tag edit, and whether the host has to agree to it before it is sent. */
export interface TagEditPlan {
  /** The PATCH body. EMPTY when there is nothing worth sending. */
  patch: TagPatchFields;
  /** '' when it can go straight out; otherwise what the host is about to lose. */
  confirm: string;
}

type EditableTag = Pick<GuestTag, 'label' | 'kind' | 'isCritical'>;

/**
 * What changing a tag's kind writes, and what has to be agreed to first.
 *
 * A critical tag leaving dietary or access cannot stay critical — the server
 * refuses the merged pair, and refuses it deliberately: "quietly un-criticalling
 * an allergy is the failure this whole design is trying to prevent". So the
 * clearing is legal to send, but only as something the host chose. Composing
 * `isCritical: false` automatically turns that refusal into an invisible side
 * effect of a <select>, which is the same failure wearing a 200.
 *
 * A kind change that strands nothing, and a pick of the kind the tag already
 * has, need no confirmation and no round trip respectively.
 */
export function planTagKindChange(tag: EditableTag, nextKind: string): TagEditPlan {
  const kind = asTagKind(nextKind);
  if (kind === asTagKind(tag.kind)) return { patch: {}, confirm: '' };
  if (!tag.isCritical || isHealthKind(kind)) return { patch: { kind }, confirm: '' };
  return {
    patch: { kind, isCritical: false },
    confirm:
      `“${tag.label}” is on the kitchen sheet. Making it a ${TAG_KIND_LABELS[kind]} tag `
      + 'takes it off, and only a food or access tag can be put back on it. There is no undo.',
  };
}

/**
 * What ticking a tag on or off the kitchen sheet writes.
 *
 * Marking one critical is additive and goes straight out. Clearing one is the
 * destructive direction — the chip loses its outline, it stops sorting first,
 * and the three-chip row cap is only safe BECAUSE critical tags sort first — so
 * it is confirmed, in words naming the tag.
 *
 * Setting the flag on a kind that cannot carry it composes no request at all.
 * The server would refuse it, and a refusal the host reads as an error is worse
 * than a control that was never offered.
 */
/**
 * Whether a plan may go on the wire now, or is waiting on the host.
 *
 * The gate itself, in the library, because it is the safety property this whole
 * feature turns on and a gate that lives only in a component cannot be pinned:
 * defeating it there left 676 tests green. A plan carrying a confirmation is a
 * question, not an instruction — un-criticalling an allergy without the host
 * reading a sentence first is the failure the server refuses a bare kind change
 * to prevent, and a client that sent anyway would reopen it from the other side.
 *
 * An empty patch is also held back: a pick of the kind a tag already has is a
 * no-op, and sending it spends a write ticket to change nothing.
 */
export function tagEditSendsNow(plan: TagEditPlan): boolean {
  if (plan.confirm) return false;
  return Object.keys(plan.patch).length > 0;
}

export function planCriticalChange(tag: EditableTag, next: boolean): TagEditPlan {
  if (next === Boolean(tag.isCritical)) return { patch: {}, confirm: '' };
  if (next) {
    return isHealthKind(asTagKind(tag.kind))
      ? { patch: { isCritical: true }, confirm: '' }
      : { patch: {}, confirm: '' };
  }
  return {
    patch: { isCritical: false },
    confirm:
      `“${tag.label}” comes off the kitchen sheet. It stays a `
      + `${TAG_KIND_LABELS[asTagKind(tag.kind)]} tag, but nothing will flag it to whoever is `
      + 'cooking until you tick it back on.',
  };
}

/**
 * A stable colour for a tag the host never coloured.
 *
 * Derived from the slug rather than from the tag's position, so a tag keeps its
 * colour when another is deleted above it — a chip column that reshuffles its
 * colours after a delete is a chip column nobody learns to read.
 */
const TAG_PALETTE = [
  '#3f6d5a', '#7a5230', '#3d5a8a', '#6b4576', '#8a4b3f',
  '#41666b', '#6b6330', '#5a4a86', '#79405c', '#3f6b45',
] as const;

export function tagColor(tag: Pick<GuestTag, 'slug' | 'color'>): string {
  const chosen = (tag.color ?? '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(chosen)) return chosen.toLowerCase();
  let hash = 0;
  for (const char of tag.slug) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length];
}

/** Tags in the order the palette and the chip column show them. */
export function sortTags(tags: ReadonlyArray<GuestTag>): GuestTag[] {
  return [...tags].sort((a, b) => {
    const kindA = TAG_KINDS.indexOf(asTagKind(a.kind));
    const kindB = TAG_KINDS.indexOf(asTagKind(b.kind));
    if (kindA !== kindB) return kindA - kindB;
    // Critical first inside a kind: an allergy outranks a preference.
    if (a.isCritical !== b.isCritical) return a.isCritical ? -1 : 1;
    if (a.position !== b.position) return a.position - b.position;
    return a.label.localeCompare(b.label);
  });
}

export interface TagKindGroup {
  kind: TagKind;
  label: string;
  tags: GuestTag[];
}

/** The palette's sections. Empty kinds are dropped rather than shown as headings. */
export function groupTagsByKind(tags: ReadonlyArray<GuestTag>): TagKindGroup[] {
  const sorted = sortTags(tags);
  return TAG_KINDS.map((kind) => ({
    kind,
    label: TAG_KIND_LABELS[kind],
    tags: sorted.filter((tag) => asTagKind(tag.kind) === kind),
  })).filter((group) => group.tags.length > 0);
}

/** How many guests carry each tag, keyed by tag id. */
export function tagCounts(
  guests: ReadonlyArray<Pick<TrackerGuest, 'tagIds'>>,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const guest of guests) {
    for (const id of guest.tagIds ?? []) counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

/**
 * The tags matching what the host typed in the palette.
 *
 * Matched against the label AND the slug, so typing "gluten free" finds
 * `gluten_free` — a host searching a tag they created by typing "Gluten Free"
 * should not have to know it is stored with an underscore.
 */
export function searchTags(tags: ReadonlyArray<GuestTag>, query: string): GuestTag[] {
  const typed = query.trim().toLowerCase();
  const sorted = sortTags(tags);
  if (!typed) return sorted;
  const slugged = tagSlug(typed);
  return sorted.filter(
    (tag) => tag.label.toLowerCase().includes(typed) || (slugged !== '' && tag.slug.includes(slugged)),
  );
}

/**
 * Whether the palette should offer "Create «Rugby lot»" as its last row.
 *
 * Only when nothing already carries that exact slug: offering to create a tag
 * that exists is how a host ends up believing they have two, and the API is
 * idempotent on slug so the click would silently do nothing visible.
 */
export function canCreateTag(tags: ReadonlyArray<GuestTag>, query: string): boolean {
  const typed = query.trim();
  if (!typed) return false;
  const slug = tagSlug(typed);
  if (!slug || slug.length > TAG_SLUG_MAX || typed.length > TAG_LABEL_MAX) return false;
  return !tags.some((tag) => tag.slug === slug);
}

export interface TagAssignPlan {
  mode: TagAssignMode;
  tagIds: string[];
  inviteeIds: string[];
  /** The guests this would actually change — the rest are already in that state. */
  changingInviteeIds: string[];
  /** Why the request is refused, in words a host can act on. '' when it is fine. */
  refusal: string;
}

/**
 * What a bulk apply would do, computed before it is sent.
 *
 * Add and remove only. A `replace` mode existed here, scoped to the kinds
 * present in `tagIds` so that setting a dietary tag could not wipe "college
 * friends". That scoping made it survivable rather than safe: replacing the
 * dietary kind across a selected cohort still clears every other dietary tag
 * those guests carry, and the tag most likely to be standing alone on somebody
 * is an allergy. There is no undo, so the loss is invisible until a kitchen
 * sheet is a line short. Setting one guest's tags is remove then add.
 */
export function planTagAssignment(
  mode: TagAssignMode,
  tagIds: ReadonlyArray<string>,
  guests: ReadonlyArray<Pick<TrackerGuest, 'inviteeId' | 'tagIds'>>,
  vocabulary: ReadonlyArray<GuestTag>,
): TagAssignPlan {
  const applied = [...new Set(tagIds)].filter(Boolean);
  const inviteeIds = guests.map((guest) => guest.inviteeId);

  const changingInviteeIds = guests
    .filter((guest) => {
      const held = new Set(guest.tagIds ?? []);
      // Two modes, so this is exhaustive: remove changes a guest who holds any
      // of the tags, add changes one who is missing any of them.
      if (mode === 'remove') return applied.some((id) => held.has(id));
      return applied.some((id) => !held.has(id));
    })
    .map((guest) => guest.inviteeId);

  let refusal = '';
  if (applied.length === 0) refusal = 'Pick at least one tag.';
  else if (applied.length > TAG_ASSIGN_MAX_TAGS) {
    refusal = `Apply at most ${TAG_ASSIGN_MAX_TAGS} tags at a time.`;
  } else if (inviteeIds.length === 0) refusal = 'Pick at least one guest.';
  else if (inviteeIds.length > TAG_ASSIGN_MAX_GUESTS) {
    refusal = `Tag at most ${TAG_ASSIGN_MAX_GUESTS} guests at a time — narrow the list first.`;
  }

  return { mode, tagIds: applied, inviteeIds, changingInviteeIds, refusal };
}

/**
 * The sentence shown on the bulk button's confirmation.
 *
 * It names the number that will CHANGE as well as the number targeted, because
 * "Tag 137 guests" on a roster where 130 already carry the tag reads as a much
 * bigger action than it is — and a host who cannot tell the difference stops
 * trusting the button.
 */
export function assignmentSentence(plan: TagAssignPlan, tags: ReadonlyArray<GuestTag>): string {
  const names = plan.tagIds
    .map((id) => tags.find((tag) => tag.id === id)?.label)
    .filter(Boolean)
    .join(', ');
  const guests = `${plan.inviteeIds.length} ${plan.inviteeIds.length === 1 ? 'guest' : 'guests'}`;
  const changing = plan.changingInviteeIds.length;

  if (plan.mode === 'remove') {
    return changing === 0
      ? `None of those ${guests} have ${names}.`
      : `Remove ${names} from ${changing} of ${guests}.`;
  }
  return changing === 0
    ? `All ${guests} already have ${names}.`
    : `Add ${names} to ${changing} of ${guests}.`;
}

/** What `POST /tags/assign` answers with, as far as the sentence below cares. */
export interface TagAssignOutcome {
  assigned: number;
  removed: number;
  skipped: number;
  /** Affected guest id -> the tag ids that guest now carries. */
  guests?: Record<string, ReadonlyArray<string>>;
}

/**
 * What a bulk apply did, in the units it actually counts.
 *
 * `assigned`, `removed` and `skipped` are (guest × tag) PAIRS: the server
 * writes one row per pair, so two tags across twelve guests is 24 and the
 * palette allows ten tags at once. The sentence the host reads BEFORE the click
 * counts guests — "Add Vegetarian, Gluten free to 12 of 12 guests." — so
 * reporting the pairs as people ("24 tagged.") leaves them two numbers that
 * cannot be reconciled and no way to tell which is wrong.
 *
 * The guest count comes off the response's own map of affected guests, not off
 * what was targeted, for the same reason the numbers do: it is an account of
 * what was written.
 */
export function assignOutcomeSentence(outcome: TagAssignOutcome): string {
  const { assigned, removed, skipped } = outcome;
  const parts = [
    assigned > 0 ? `${assigned} tag${assigned === 1 ? '' : 's'} added` : '',
    removed > 0 ? `${removed} removed` : '',
    skipped > 0 ? `${skipped} already as you wanted` : '',
  ].filter(Boolean);
  if (parts.length === 0) return 'Nothing needed changing.';
  const people = Object.keys(outcome.guests ?? {}).length;
  // A write that changed nobody has no guests to count — an apply that was
  // entirely skips is the ordinary case for that.
  if (people === 0) return `${parts.join(', ')}.`;
  return `${parts.join(', ')}, across ${people} ${people === 1 ? 'guest' : 'guests'}.`;
}

/**
 * Whether this API knows about tags at all.
 *
 * Studio and the concierge API deploy separately, and a tag row that renders
 * against an API with no tag routes is a control that silently does nothing —
 * the exact failure this codebase keeps getting burned by. The board's `tags`
 * key is the probe: the API sends it (possibly empty) once tags exist, and
 * omits it entirely before that. No extra round trip, and no guess.
 */
export function tagsAvailable(board: { tags?: unknown } | null | undefined): boolean {
  return Array.isArray(board?.tags);
}

/** What to say instead of the tag controls when the API predates them. */
export const TAGS_UNAVAILABLE_NOTE =
  'Guest tags need a newer version of the trips API than this one. Nothing here is broken — the controls are hidden rather than shown doing nothing.';

/**
 * Splits one imported cell into tag labels.
 *
 * Semicolon, pipe and comma all appear as the separator in real files, and a
 * host who typed "vegetarian, no nuts" means two tags. Empty pieces are dropped
 * rather than becoming a blank tag, and duplicates inside one cell collapse —
 * "GF, gluten free" is one tag once slugged, and importing it twice would
 * otherwise show the host "creates 2 new tags" for one.
 */
export function splitTagCell(cell: string | null | undefined): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const piece of String(cell ?? '').split(/[;|,]/)) {
    const label = normalizeCsvText(piece);
    const slug = tagSlug(label);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    labels.push(label);
  }
  return labels;
}

export interface NewTagPreview {
  /** Slugs in the file that no tag on this trip carries yet. */
  newSlugs: string[];
  /** Guests in the file that carry at least one tag column value. */
  taggedRows: number;
}

/**
 * What importing this file would add to the vocabulary, so the host sees
 * "creates 4 new tags" BEFORE confirming rather than discovering forty of them
 * afterwards. A typo in a spreadsheet column is a new tag, and this is the only
 * place it is cheap to catch.
 */
export function previewNewTags(
  rows: ReadonlyArray<{ tags?: string | null; dietary?: string | null }>,
  existing: ReadonlyArray<GuestTag>,
): NewTagPreview {
  const known = new Set(existing.map((tag) => tag.slug));
  const newSlugs: string[] = [];
  let taggedRows = 0;

  for (const row of rows) {
    const labels = [...splitTagCell(row.tags), ...splitTagCell(row.dietary)];
    if (labels.length > 0) taggedRows += 1;
    for (const label of labels) {
      const slug = tagSlug(label);
      if (!slug || known.has(slug)) continue;
      known.add(slug);
      newSlugs.push(slug);
    }
  }

  return { newSlugs, taggedRows };
}

/** "creates 4 new tags: rugby lot, work, …" — or '' when it creates none. */
export function newTagsSentence(preview: NewTagPreview): string {
  if (preview.newSlugs.length === 0) return '';
  const shown = preview.newSlugs.slice(0, 5).join(', ');
  const rest = preview.newSlugs.length - 5;
  const noun = preview.newSlugs.length === 1 ? 'new tag' : 'new tags';
  return `Creates ${preview.newSlugs.length} ${noun}: ${shown}${rest > 0 ? `, and ${rest} more` : ''}.`;
}

/** What one roster row shows: a few chips, then a count of what did not fit. */
export interface RowTagChips {
  shown: GuestTag[];
  overflow: number;
  /** Every tag on the row, for the chip strip's title attribute. */
  allLabels: string[];
}

/**
 * The chips for one guest, capped.
 *
 * A host who tags every household individually gets forty tags on a 137-guest
 * roster, and an uncapped chip column makes the table unreadable — so the cap
 * is the mitigation for that, not a styling preference. Critical tags sort
 * first (see {@link sortTags}), which is what makes the cap safe: the three
 * that survive it are the three that matter.
 */
export function rowTagChips(
  tagIds: ReadonlyArray<string> | null | undefined,
  vocabulary: ReadonlyArray<GuestTag>,
  max = 3,
): RowTagChips {
  const byId = new Map(vocabulary.map((tag) => [tag.id, tag]));
  const held = sortTags(
    (tagIds ?? []).map((id) => byId.get(id)).filter((tag): tag is GuestTag => Boolean(tag)),
  );
  return {
    shown: held.slice(0, max),
    overflow: Math.max(0, held.length - max),
    allLabels: held.map((tag) => tag.label),
  };
}
